// Typed API client for the pricing-intel Functions.

export interface Spot { gold?: number; platinum?: number; palladium?: number; silver?: number }

export interface VariantMetrics {
  weight_grams: number | null
  weight_is_estimated: boolean
  assumed_depth_mm: number | null
  base_metal: string | null
  purity_factor: number | null
  spot_aud_per_gram: number | null
  metal_value_aud: number | null
  price_per_gram: number | null
  melt_premium_aud: number | null
  melt_premium_pct: number | null
}

export interface MetricVariant {
  variant_id: number
  competitor_id: number
  competitor_name: string
  positioning_tier: string | null
  is_custom: number
  product_name: string | null
  source_url: string
  metal: string | null
  design_profile: string | null
  band_width_mm: number | null
  band_depth_mm: number | null
  finger_size: string | null
  finger_size_us: number | null
  metal_weight_grams: number | null
  price_aud: number | null
  price_type: string | null
  metrics: VariantMetrics
}

export interface Dashboard {
  ok: boolean
  spot: Spot
  totals: {
    competitors: number; active_variants: number; priced_variants: number
    poa_count: number; estimated_weight_count: number
  }
  median_price_by_metal: { metal: string; median_price: number | null; n: number }[]
  custom_vs_ready: {
    custom: { n: number; median: number | null }
    ready: { n: number; median: number | null }
  }
  melt_premium_league: {
    competitor_id: number; name: string; tier: string | null
    median_premium: number | null; n_premium: number; n_priced: number; poa_pct: number
  }[]
  distribution: {
    competitor_name: string; positioning_tier: string | null; metal: string | null
    is_custom: number; band_width_mm: number | null; price_aud: number | null
    melt_premium_aud: number | null
  }[]
  movers: unknown[]
}

export interface Competitor {
  id: number; name: string; website_url: string; location: string | null
  positioning_tier: string | null; active: number
  product_count: number; variant_count: number; poa_count: number
}

export interface CompetitorRow {
  id: number; name: string; website_url: string; location: string | null
  positioning_tier: string | null; positioning_notes: string | null
  scrape_config: string | null; active: number; created_at: string
}
export interface CompetitorProfile {
  ok: boolean
  competitor: CompetitorRow
  aggregates: { product_count: number; variant_count: number; min_price: number | null; max_price: number | null; poa_count: number; no_weight_count: number }
  metal_mix: { metal: string; n: number }[]
}
export interface ScrapeRun {
  id: number; competitor_id: number | null; competitor_name: string | null
  started_at: string; completed_at: string | null; pages_fetched: number
  products_found: number; products_new: number; products_delisted: number
  status: string; errors: string | null
}
export interface AuditProduct {
  id: number; source_url: string; product_name: string | null
  extraction_method: string | null; raw_source: string | null
  variants: { metal: string | null; price_aud: number | null; band_width_mm: number | null; raw_extraction: string | null; weight_is_estimated: number; price_type: string | null; metal_weight_grams: number | null; finish: string | null; design_profile: string | null; finger_size: string | null }[]
}
export interface Trends {
  ok: boolean; metals: string[]
  price_series: Record<string, number | string | null>[]
  spot_series: Record<string, number | string>[]
}

// Surface the server's own error message (Functions return `{ ok:false, error }`)
// instead of a bare status code, so a 502 says *why* it failed.
async function readError(url: string, res: Response): Promise<Error> {
  let detail = ''
  try {
    const body = (await res.clone().json()) as { error?: string }
    if (body?.error) detail = `: ${body.error}`
  } catch {
    try {
      const text = (await res.text()).trim()
      if (text) detail = `: ${text.slice(0, 300)}`
    } catch { /* ignore */ }
  }
  return new Error(`${url} → ${res.status}${detail}`)
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) throw await readError(url, res)
  return res.json() as Promise<T>
}
async function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, { method, headers: { 'content-type': 'application/json' }, body: body ? JSON.stringify(body) : undefined })
  if (!res.ok) throw await readError(url, res)
  return res.json() as Promise<T>
}

export const fetchDashboard = () => get<Dashboard>('/api/dashboard')
export const fetchCompetitors = () => get<{ ok: boolean; competitors: Competitor[] }>('/api/admin/competitors')
export const fetchMetrics = (params: Record<string, string>) => {
  const q = new URLSearchParams(params).toString()
  return get<{ ok: boolean; spot: Spot; count: number; poa_count: number; estimated_weight_count: number; variants: MetricVariant[] }>(
    `/api/metrics${q ? `?${q}` : ''}`,
  )
}
export const fetchTrends = () => get<Trends>('/api/trends')
export const fetchCompetitorProfile = (id: number) => get<CompetitorProfile>(`/api/competitors/${id}`)
export const patchCompetitor = (id: number, body: Record<string, unknown>) =>
  send<{ ok: boolean; competitor: CompetitorRow }>(`/api/competitors/${id}`, 'PATCH', body)
export const deleteCompetitor = (id: number) => send<{ ok: boolean }>(`/api/competitors/${id}`, 'DELETE')
export const createCompetitor = (c: Record<string, unknown>) =>
  send<{ ok: boolean; results: unknown[] }>('/api/admin/seed', 'POST', { competitors: [c] })
export const fetchScrapeRuns = () => get<{ ok: boolean; runs: ScrapeRun[] }>('/api/admin/scrape-runs')
export const triggerScrape = (competitor_id: number) =>
  send<{ ok: boolean; summary?: unknown; error?: string }>('/api/admin/scrape', 'POST', { competitor_id })
export const refreshSpot = (body: Record<string, unknown>) =>
  send<{ ok: boolean; latest?: Spot; error?: string }>('/api/admin/refresh-spot', 'POST', body)
export const fetchAudit = (competitorId: number) =>
  get<{ ok: boolean; count: number; products: AuditProduct[] }>(`/api/admin/audit?competitor_id=${competitorId}`)
